import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import StatusBadge from '../components/StatusBadge';
import { STATUS_LABELS } from '../utils/statuses';
import * as XLSX from 'xlsx';

export default function ColleagueDashboard() {
  const { user, logout } = useAuth();
  const [parts, setParts] = useState([]);
  const [filters, setFilters] = useState({ status: '' });
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, active: 0, debt: 0 });

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      const res = await api.get('/api/parts', { params });
      const data = res.data;
      setParts(data);
      setSummary({
        total: data.length,
        active: data.filter(p => p.status !== 'sent').length,
        debt: data.filter(p => p.payment_status === 'unpaid' && p.repair_cost > 0).reduce((acc, p) => acc + Number(p.repair_cost), 0),
      });
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchParts(); }, [fetchParts]);

  const exportExcel = async () => {
    try {
      const res = await api.get('/api/parts/export/my');
      const statusLabels = { received: 'دریافت شد', in_progress: 'در حال پیگیری', repaired: 'تعمیر شد', irreparable: 'غیرقابل تعمیر', warranty: 'شامل گارانتی', replaced: 'تعویض شد', not_visible: 'مشکل مشاهده نشد', sent: 'ارسال شد' };
      const data = res.data.map(p => ({
        'شناسه': p.id, 'نام قطعه': p.part_name, 'وضعیت': statusLabels[p.status] || p.status,
        'هزینه (تومان)': p.repair_cost || 0, 'پرداخت': p.payment_status === 'paid' ? 'پرداخت شده' : 'پرداخت نشده',
        'تاریخ ثبت': p.created_date,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'قطعات من');
      XLSX.writeFile(wb, 'قطعات-من.xlsx');
    } catch (err) { alert('خطا در دریافت داده'); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Vazirmatn,Tahoma,sans-serif', direction: 'rtl' }}>
      <header style={{ background: 'linear-gradient(135deg,#1a1a2e,#0f3460)', color: '#fff', padding: '0 1.5rem', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>🔧 قطعات من</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '0.9rem', opacity: 0.85 }}>{user?.name}</span>
          <button onClick={logout} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}>خروج</button>
        </div>
      </header>

      <main style={{ padding: '1.5rem', maxWidth: 700, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
          {[['کل قطعات', summary.total, '#0f3460'], ['در جریان', summary.active, '#0f3460'], ['بدهی (ت)', summary.debt.toLocaleString('fa-IR'), summary.debt > 0 ? '#dc2626' : '#059669']].map(([l, v, c]) => (
            <div key={l} style={{ background: '#fff', borderRadius: 12, padding: '14px', textAlign: 'center', borderTop: `3px solid ${c}`, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: c }}>{v}</div>
              <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontFamily: 'inherit', fontSize: '0.9rem', background: '#fff' }} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">همه وضعیت‌ها</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={exportExcel} style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.88rem', fontWeight: 600 }}>📥 خروجی Excel</button>
        </div>

        <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 10 }}>{parts.length} قطعه</p>

        {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>در حال بارگذاری...</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {parts.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>قطعه‌ای یافت نشد</div>}
            {parts.map(part => (
              <div key={part.id} style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,.07)', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>{part.part_name}</span>
                  <StatusBadge status={part.status} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>ثبت: {new Date(part.created_at).toLocaleDateString('fa-IR')}</span>
                  {part.repair_cost > 0 && (
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: part.payment_status === 'paid' ? '#059669' : '#dc2626' }}>
                      {Number(part.repair_cost).toLocaleString('fa-IR')} تومان {part.payment_status === 'paid' ? '✓' : '(پرداخت نشده)'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
